import { Component } from 'react'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'
import classNames from 'classnames'
import { AutoSizer } from 'react-virtualized'

import withStyles from 'beam/dist/styles'
import Typography from 'beam/dist/Typography'
import { CircularProgress } from 'beam/dist/Progress'
import { ConditionalWrap } from 'beam/dist/utils'

import DesktopTape from './DesktopTape'

import PlayerPiano from '../PlayerPiano/PlayerPiano'
import { PANEL_SIZES } from '../MediaPlayer/helpers'

const styles = theme => ({
  root: {
    flex: '1 1 auto',
    display: 'flex',
  },
  rootNoData: {
    justifyContent: 'center',
    alignItems: 'center',
    flexGrow: 1,
  },
  rootYesData: {},
  colorBlocks: {
    display: 'flex',
  },
  svg: {
    display: 'block',
  },
  block: {
    fill: theme.palette.common.black,
    stroke: theme.palette.common.white,
    opacity: 1,
  },
  blockText: {
    fontSize: '12px',
    color: theme.palette.text.primary,
  },
  event: { fill: theme.palette.color.orange[200], stroke: theme.palette.color.orange[200] },
  inActive: { fill: theme.palette.common.white },
  onHold: { fill: theme.palette.color.deepOrange[700] },
  afterCallWork: { fill: theme.palette.color.purple[900] },
  active: { fill: theme.palette.color.lightBlue[800] },
  unapproved: { fill: theme.palette.color.red[700] },
  approved: { fill: theme.palette.color.grey[400] }, // Will be blue (theme.palette.color.lightBlue[800]) again once approved and undefined is differentiated
})

class DesktopContent extends Component {
  static propTypes = {
    theme: PropTypes.object,
    /** Provided by `react-i18next.translate` decorator */
    t: PropTypes.func.isRequired,
    /** Provided by `beam/dist/styles.withSytles` decorator */
    classes: PropTypes.object.isRequired,
    /** contact id */
    ccrid: PropTypes.number,
    /** triggers API request to get the data */
    updateContactId: PropTypes.func.isRequired,
    /** if the transcription data is still being loaded */
    loading: PropTypes.bool.isRequired,
    /** if loading the transcription data failed for some reason */
    error: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]).isRequired,
    /** event data for the given call */
    events: PropTypes.array,
    /** Interaction states for given call */
    interactionStates: PropTypes.array,
    /** Application usage for given call */
    appUse: PropTypes.array,
    /** what time the call started */
    startTime: PropTypes.number,
    /** Time to seek the video to in milliseconds */
    currentTime: PropTypes.number,
    /** Length of the call */
    callDuration: PropTypes.number,
    /** Callback for time updates */
    setTime: PropTypes.func.isRequired,
    zoomed: PropTypes.bool,
    showEvents: PropTypes.bool,
    isRootRecording: PropTypes.bool,
  }

  static defaultProps = {
    events: [],
    interactionStates: [],
    appUse: [],
    startTime: 0,
    currentTime: 0,
    callDuration: 0,
    zoomed: true,
    showEvents: true,
    isRootRecording: false,
  }

  componentDidMount() {
    const { updateContactId, ccrid, isRootRecording } = this.props
    updateContactId(ccrid, isRootRecording)
  }

  componentDidUpdate(prevProps) {
    const { updateContactId, ccrid, isRootRecording } = this.props
    if (ccrid && (prevProps.ccrid !== ccrid || prevProps.isRootRecording !== isRootRecording)) {
      updateContactId(ccrid, isRootRecording)
    }
  }

  getBarClass = type => {
    const { classes } = this.props
    switch (type) {
      case 'EVENT':
        return classes.event
      case 'Inactive':
        return classes.inActive
      case 'OnHold':
        return classes.onHold
      case 'AfterCallWork':
        return classes.afterCallWork
      case 'Active':
      default:
        return classes.active
    }
  }

  getTypeLabel = type => {
    const { t } = this.props
    switch (type) {
      case 'OnHold':
        return t('onHold')
      case 'Active':
        return t('active')
      case 'Inactive':
        return t('inactive')
      case 'AfterCallWork':
        return t('afterCallWork')
      default:
        return type
    }
  }

  renderLoading() {
    return <CircularProgress />
  }

  renderError() {
    const { t } = this.props
    return (
      <div>
        <Typography color="secondary" align="center">
          {t('error')}
        </Typography>
        <Typography color="secondary" align="center">
          {t('desktopDataLoadError')}
        </Typography>
      </div>
    )
  }

  renderNoData() {
    const { t } = this.props
    return <Typography align="center">{t('noDesktopData')}</Typography>
  }

  renderData() {
    const {
      theme,
      startTime,
      events,
      interactionStates,
      appUse,
      setTime,
      callDuration,
      currentTime,
      zoomed,
      showEvents,
    } = this.props

    const panelWidth = theme.spacing(PANEL_SIZES.DESKTOP)

    return (
      <AutoSizer disableWidth>
        {({ height }) => {
          //If zoomed a pixel every 1/10th of a second
          const graphHeight = zoomed ? Math.max(callDuration / 100, height) : height
          const heightUnitsPerMs = graphHeight / callDuration

          return (
            <div style={{ height: `${height}px`, width: `${panelWidth}px` }}>
              <ConditionalWrap
                condition={zoomed}
                wrap={children => (
                  <PlayerPiano
                    duration={callDuration}
                    currentTime={currentTime}
                  >
                    {children}
                  </PlayerPiano>
                )}
              >
                <DesktopTape
                  startTime={startTime}
                  events={events}
                  interactionStates={interactionStates}
                  appUse={appUse}
                  setTime={setTime}
                  callDuration={callDuration}
                  zoomed={zoomed}
                  showEvents={showEvents}
                  graphHeight={graphHeight}
                  width={panelWidth}
                  heightUnitsPerMs={heightUnitsPerMs}
                />
              </ConditionalWrap>
            </div>
          )
        }}
      </AutoSizer>
    )
  }

  render() {
    const { classes, loading, error, appUse } = this.props

    const noData = loading || error || appUse.length === 0

    return (
      <div className={classNames(classes.root, noData ? classes.rootNoData : classes.rootYesData)}>
        {(loading && this.renderLoading()) ||
          (error && this.renderError()) ||
          (noData && this.renderNoData()) ||
          this.renderData()}
      </div>
    )
  }
}

export { DesktopContent as UnstyledDesktopContent }
export default withTranslation()(withStyles(styles, { withTheme: true })(DesktopContent))
